import {
  toLineBreak,
  toLinkContent,
  toParagraphContent,
} from '@principle-theorem/editor';
import {
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
  RecurrencePattern,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  MarketingCampaignStepConversionType,
  MarketingCampaignType,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  coldContactReactivationCampaignReportTemplate,
  FactTables,
} from '@principle-theorem/reporting';
import {
  ALL_REGIONS,
  asReffable,
  CustomRecurrenceFrequency,
  DayOfWeek,
  DocumentReference,
  Frequency,
} from '@principle-theorem/shared';
import { CampaignTemplateHelpers } from './campaign-template-helpers';
import { IMarketingCampaignTemplate } from './marketing-campaign-template';
import { MarketingCampaignTemplateFactory } from './marketing-campaign-template-factory';

export function getColdContactReactivationCampaignTemplate(
  practiceRef: DocumentReference<IPractice>
): IMarketingCampaignTemplate {
  return {
    name: 'Cold Contact Reactivation (24 month+)',
    description: `A campaign targeting patients who haven't visited in over 24 months, encouraging them to re-engage with the practice and book an appointment.`,
    regions: ALL_REGIONS,
    campaign: MarketingCampaign.init({
      brandRef: Practice.brandRef(asReffable(practiceRef)),
      practiceRef,
      name: 'Cold Contact Reactivation (24 month+)',
      type: MarketingCampaignType.Marketing,
      scope: TemplateScope.Patient,
      entryResetRules: {
        multipleEntry: true,
        reentryScope: TemplateScope.Patient,
        maxEntryCount: undefined,
        frequencyCount: 6,
        frequencyType: CustomRecurrenceFrequency.Monthly,
      },
      conversionType: MarketingCampaignStepConversionType.AnyAppointmentBooked,
      recurrencePattern: RecurrencePattern.init({
        customFrequencyType: CustomRecurrenceFrequency.Weekly,
        daysOfWeek: [DayOfWeek.Monday],
        frequencyType: Frequency.Custom,
      }),
      recurrenceRunTime: '08:32',
    }),
    report: coldContactReactivationCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    steps: [
      MarketingCampaignStep.init({
        name: 'Initial Contact - SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, it’s {{ practiceName }}. Just checking in — we haven’t seen you in a while!',
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  '[INSERT PRACTICE OFFERING HERE]'
                ),
                CampaignTemplateHelpers.highlight(
                  '[INSERT UNIQUE OR NEW INFO HERE]'
                ),
                toLineBreak(),
                'Book here: {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}.',
                toLineBreak(),
                'If you’d prefer not to receive messages from us, you can unsubscribe here: {{ unsubscribeLink }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 3,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Initial Contact - Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'A friendly check-in from the team at {{ practiceName }}',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'It’s been a little while since your last visit, and we simply wanted to check in to see how you’ve been.',
                toLineBreak(),
                'If you’re already seeing another dentist, that’s wonderful! The most important thing is that your smile is being looked after. But if it’s been some time, this might be a great moment for a quick check-up to keep everything healthy.',
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  '[INSERT SOME KIND OF RETURNING PATIENT OFFER TO ATTRACT THEM TO THE PRACTICE]'
                ),
                CampaignTemplateHelpers.highlight(
                  '[INSERT SOMETHING UNIQUE OR NEW ABOUT YOUR PRACTICE]'
                ),
                CampaignTemplateHelpers.highlight(
                  '[INSERT PAYMENT PLAN OPTIONS OR HEALTH FUND / MEMBERSHIP DETAILS]'
                ),
                toLineBreak(),
                [
                  '👉',
                  toLinkContent('Book here', '{{ appointmentBookingLink }}'),
                  ' or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                  '.',
                ],
                toLineBreak(),
                [
                  'If you’d prefer not to receive messages from us, you can ',
                  toLinkContent('unsubscribe here', '{{ unsubscribeLink }}'),
                  '.',
                ],
                toLineBreak(),
                'Warm regards,',
                'The {{ practiceName }} Team',
                [
                  CampaignTemplateHelpers.highlight('[WEBSITE URL]'),
                  ' | ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Days,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Second Contact - SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, just a friendly note from {{ practiceName }}',
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  '[INSERT REACTIVATION OFFER]'
                ),
                CampaignTemplateHelpers.highlight(
                  '[INSERT PAYMENT, HEALTH FUND, OR MEMBERSHIP INFORMATION]'
                ),
                toLineBreak(),
                'Book here: {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}.',
                toLineBreak(),
                'If you’d prefer not to receive messages from us, you can unsubscribe here: {{ unsubscribeLink }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 5,
            unit: TimingUnit.Days,
            time: '16:48',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Third Contact - Email',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'An update and reminder form your local caring Dentists, {{ practiceName }}',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'We’ve been busy growing and improving at {{ practiceName }}!',
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  'INCLUDE ANY NEW OFFERINGS, NEW DENTISTS, OR OTHER THINGS OF INTEREST HERE]'
                ),
                CampaignTemplateHelpers.highlight(
                  'INCLUDE PATIENT REACTIVATION OFFER]'
                ),
                toLineBreak(),
                'We truly care about your oral health — even if you’re seeing another dentist, that’s wonderful. We just wanted to make sure you’re being looked after.',
                toLineBreak(),
                [
                  '👉 ',
                  toLinkContent('Book here', '{{ appointmentBookingLink }}'),
                  ' or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                  '.',
                ],
                toLineBreak(),
                'Warm regards,',
                'The {{ practiceName }} Team',
                [
                  CampaignTemplateHelpers.highlight('[WEBSITE URL]'),
                  ' | ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                toLineBreak(),
                [
                  'P.S. If you’d prefer not to receive future messages, you can ',
                  toLinkContent('unsubscribe here', '{{ unsubscribeLink }}'),
                  '.',
                ],
                toLineBreak(),
                CampaignTemplateHelpers.highlight('[INSERT LOGO HERE]'),
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 5,
            unit: TimingUnit.Days,
            time: '08:47',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Third Contact - SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, it’s {{ practiceName }}! It has been a while since we saw you, and you may have missed some of our previous messages. We just want to make sure your oral health is being looked after.',
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  '[INSERT REACTIVATION OFFER]'
                ),
                toLineBreak(),
                [
                  'Book here: ',
                  toLinkContent('{{ appointmentBookingLink }}'),
                  ' or call ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                  '.',
                ],
                toLineBreak(),
                'If you’d prefer not to receive messages from us, you can unsubscribe here: {{ unsubscribeLink }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 3,
            unit: TimingUnit.Hours,
            // time: '16:48',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Final Contact - SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }}, it’s {{ practiceName }}. Just a final little check-in - we hope you’ve been well!',
                [
                  'If you haven’t had a check-up lately, our ',
                  CampaignTemplateHelpers.highlight(
                    '[INSERT REACTIVATION OFFER HERE]'
                  ),
                  ' is still available.',
                ],
                'We’re always here if you need us 💙',
                'Book here: {{ appointmentBookingLink }} or call {{ practicePhoneNumber }}.',
                toLineBreak(),
                'If you’d prefer not to receive messages from us, you can unsubscribe here: {{ unsubscribeLink }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 8,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Final Contact - Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'A final little check-in from us at {{ practiceName }}',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'We just wanted to reach out one last time to check in and make sure you’re doing okay.',
                toLineBreak(),
                'If you’ve found another dentist who’s looking after you — that’s fantastic 👏 Your oral health truly matters most to us.',
                toLineBreak(),
                [
                  'If not, we’d love to welcome you back for ',
                  CampaignTemplateHelpers.highlight(
                    '[INSERT REACTIVATION OFFER HERE]'
                  ),
                  '.',
                ],
                toLineBreak(),
                CampaignTemplateHelpers.highlight(
                  '[INSERT SOME OF THE SPECIALISED TREATMENTS OR OFFERINGS YOU SUPPLY]'
                ),
                CampaignTemplateHelpers.highlight(
                  '[INSERT PAYMENT PLANS, HEALTH FUND OR FINANCIAL ASSISTANCE DETAILS HERE]'
                ),
                toLineBreak(),
                'Either way, you’re always welcome here at {{ practiceName }}.',
                [
                  '👉 ',
                  toLinkContent('Book here', '{{ appointmentBookingLink }}'),
                  ' or call us on ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                  '.',
                ],
                toLineBreak(),
                [
                  'If you’d prefer not to receive future updates, you can ',
                  toLinkContent('unsubscribe here', '{{ unsubscribeLink }}'),
                  '.',
                ],
                toLineBreak(),
                'Warm regards,',
                'The {{ practiceName }} Team',
                toLineBreak(),
                [
                  CampaignTemplateHelpers.highlight('[WEBSITE URL]'),
                  ' | ',
                  CampaignTemplateHelpers.phoneLink(
                    '{{ practicePhoneNumber }}'
                  ),
                ],
                CampaignTemplateHelpers.highlight('[INSERT LOGO HERE]'),
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 5,
            unit: TimingUnit.Days,
            time: '07:48',
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
    ],
  };
}
